// tslint:disable:no-bitwise
// tslint:disable:max-line-length

import {Injectable} from '@angular/core';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {
  DateFieldConfig, DatetimeFieldConfig,
  DynamicFormConfig,
  FieldTypeEnum,
  FilterSelectFieldConfig,
  MultiOptionConfig,
  MultipleOptionType,
  TextAreaFieldConfig,
  TextFieldConfig
} from '@mydocma/dynamic-form';
import {IPoolEditDialogData} from '../pool-edit/pool-edit.component';
import {Pool} from '@docma-mm-core/pool/pool';
import {AbstractControl} from '@angular/forms';
import {CustomValidationError} from '@mydocma/dynamic-form/lib/validation/custom-validation-error';
import {TranslateService} from '@ngx-translate/core';

export enum PoolVisibilityEnum {
  None = 0,
  SubContractor = 1,  // ct = 1
  Contractor = 2,     // co = 2
  Customer = 4        // SC = 4
}

export enum PoolVisibilityTextEnum {
  Customer = 'sc',
  Contractor = 'co',
  SubContractor = 'ct'
}

@Injectable()
export class PoolFormService {

  constructor(private readonly transalteService: TranslateService) {
  }

  getPoolFormConfig(dialogData: IPoolEditDialogData) {
    return dialogData.poolToEdit
      ? this.getEditPoolFormConfig(dialogData)
      : this.getNewPoolFormConfig(dialogData);
  }

  private getEditPoolFormConfig(dialogData: IPoolEditDialogData): DynamicFormConfig {
    const nameConfig = this.createNameConfig();
    const commentConfig = this.createCommentConfig();
    const createdOnConfig = this.createCreatedOnConfig();

    nameConfig.value = dialogData.poolToEdit.Name;
    commentConfig.value = dialogData.poolToEdit.Comment || '';

    const visibleForConfig = this.createVisibleForConfig();

    const formConfig: DynamicFormConfig = {
      fields: [
        nameConfig,
        commentConfig
      ],
      layouts: []
    };

    this.addTypeField(formConfig, dialogData.poolToEdit.IsPublic ? EntityScopeEnum.Project : EntityScopeEnum.User);
    this.addCreatorField(formConfig, `${(dialogData.poolToEdit.User?.FirstName ?? '')} ${(dialogData.poolToEdit.User?.LastName ?? '')}`);
    this.addCreatedOnField(formConfig, dialogData.poolToEdit.CreatedOn);
    this.addVisibilityField(formConfig, visibleForConfig, !dialogData.poolToEdit.IsPublic);

    return formConfig;
  }

  private getNewPoolFormConfig(dialogData: IPoolEditDialogData): DynamicFormConfig {
    const nameConfig = this.createNameConfig();
    const commentConfig = this.createCommentConfig();
    const createdOnConfig = this.createCreatedOnConfig();

    nameConfig.value = '';
    commentConfig.value = '';

    const visibleForConfig = this.createVisibleForConfig(true);

    const formConfig: DynamicFormConfig = {
      fields: [
        nameConfig,
        commentConfig
      ],
      layouts: []
    };

    this.addTypeField(formConfig, dialogData.scope);
    this.addCreatorField(formConfig, `${(dialogData.user?.FirstName ?? '')} ${(dialogData.user?.LastName ?? '')}`);
    this.addCreatedOnField(formConfig, new Date());
    this.addVisibilityField(formConfig, visibleForConfig, dialogData.scope !== EntityScopeEnum.Project);

    return formConfig;
  }

  private createNameConfig(): TextFieldConfig {
    return {
      id: 'name',
      name: 'name',
      label: {
        text: 'MM.pool.leftPane.form.name'
      },
      type: FieldTypeEnum.text,
      required: true,
      maxLength: 255,
      validators: [this.whitespaceValidator.bind(this)]
    };
  }

  private createCommentConfig(): TextAreaFieldConfig {
    return {
      id: 'comment',
      name: 'comment',
      label: {
        text: 'MM.pool.leftPane.form.comment'
      },
      type: FieldTypeEnum.textarea,
      required: false,
      rows: 3,
      minRows: 3,
      maxRows: 10,
      autosize: true,
      validators: [this.whitespaceValidator.bind(this)]
    };
  }

  private createCreatedOnConfig(): DateFieldConfig {
    return {
      id: 'createdOn',
      name: 'createdOn',
      label: {
        text: 'MM.pool.leftPane.form.createdOn'
      },
      type: FieldTypeEnum.date,
      required: false,
      readonly: true,
      class: 'mydocma-ignore-readonly'
    };
  }

  private createTypeConfig(): FilterSelectFieldConfig {
    return {
      id: 'type',
      name: 'type',
      label: {
        text: 'MM.pool.leftPane.form.type'
      },
      type: FieldTypeEnum.filterSelect,
      required: false,
      source: [
        { id: EntityScopeEnum.Project, text: 'MM.pool.leftPane.form.typeProject' },
        { id: EntityScopeEnum.User, text: 'MM.pool.leftPane.form.typeUser' }
      ],
      removeClearButton: true
    };
  }

  private createUserConfig(): TextFieldConfig {
    return {
      id: 'user',
      name: 'user',
      label: {
        text: 'MM.pool.leftPane.form.creator'
      },
      type: FieldTypeEnum.text,
      required: false,
      readonly: true,
      class: 'mydocma-ignore-readonly'
    };
  }

  private createVisibleForConfig(setDefaultValues: boolean = false): MultiOptionConfig {
    const config: MultiOptionConfig = {
      id: 'visibleFor',
      name: 'visibleFor',
      label: {
        text: 'MM.pool.leftPane.form.visibleFor'
      },
      source: {
        [PoolVisibilityTextEnum.Customer]: { text: 'MM.pool.leftPane.form.visibleForSc' },
        [PoolVisibilityTextEnum.Contractor]: { text: 'MM.pool.leftPane.form.visibleForCo' },
        [PoolVisibilityTextEnum.SubContractor]: { text: 'MM.pool.leftPane.form.visibleForCt' }
      },
      optionType: MultipleOptionType.slideToggle,
      type: FieldTypeEnum.multiOption,
      hidden: false
    };

    if (setDefaultValues) {
      config.value = {
        [PoolVisibilityTextEnum.Customer]: true,
        [PoolVisibilityTextEnum.Contractor]: true,
        [PoolVisibilityTextEnum.SubContractor]: true
      };
    }

    return config;
  }

  private configureVisibilityForEdit(poolToEdit: Pool, visibleForConfig: MultiOptionConfig): void {
    if (poolToEdit.IsPublic && poolToEdit.VisibleFor !== undefined && poolToEdit.VisibleFor !== null) {
      let visibilityValue: any;

      if (typeof poolToEdit.VisibleFor === 'number') {
        visibilityValue = {
          [PoolVisibilityTextEnum.Customer]: (poolToEdit.VisibleFor & PoolVisibilityEnum.Customer) === PoolVisibilityEnum.Customer,
          [PoolVisibilityTextEnum.Contractor]: (poolToEdit.VisibleFor & PoolVisibilityEnum.Contractor) === PoolVisibilityEnum.Contractor,
          [PoolVisibilityTextEnum.SubContractor]: (poolToEdit.VisibleFor & PoolVisibilityEnum.SubContractor) === PoolVisibilityEnum.SubContractor
        };
      } else {
        visibilityValue = {
          [PoolVisibilityTextEnum.Customer]: poolToEdit.VisibleFor.SC || false,
          [PoolVisibilityTextEnum.Contractor]: poolToEdit.VisibleFor.CO || false,
          [PoolVisibilityTextEnum.SubContractor]: poolToEdit.VisibleFor.CT || false
        };
      }

      visibleForConfig.value = visibilityValue;
    } else if (poolToEdit.IsPublic && poolToEdit.VisibleFor === 0) {
      const visibilityValue = {
        [PoolVisibilityTextEnum.Customer]: false,
        [PoolVisibilityTextEnum.Contractor]: false,
        [PoolVisibilityTextEnum.SubContractor]: false
      };
      visibleForConfig.value = visibilityValue;
    } else {
      this.setDefaultVisibilityValues(visibleForConfig);
    }
  }

  private setDefaultVisibilityValues(visibleForConfig: MultiOptionConfig): void {
    visibleForConfig.value = {
      [PoolVisibilityTextEnum.Customer]: true,
      [PoolVisibilityTextEnum.Contractor]: true,
      [PoolVisibilityTextEnum.SubContractor]: true
    };
  }

  private addTypeField(formConfig: DynamicFormConfig, value: EntityScopeEnum): void {
    const typeFieldConfig = this.createTypeConfig();
    typeFieldConfig.value = value;
    formConfig.fields.push(typeFieldConfig);
  }

  private addCreatorField(formConfig: DynamicFormConfig, value: string): void {
    const creatorFieldConfig = this.createUserConfig();
    creatorFieldConfig.value = value;
    formConfig.fields.push(creatorFieldConfig);
  }

  private addCreatedOnField(formConfig: DynamicFormConfig, value: Date): void {
    const createdOnFieldConfig = this.createCreatedOnConfig();
    createdOnFieldConfig.value = value;
    formConfig.fields.push(createdOnFieldConfig);
  }

  private addVisibilityField(formConfig: DynamicFormConfig, visibleForConfig: MultiOptionConfig, isHidden: boolean): void {
    visibleForConfig.hidden = isHidden;
    if (!isHidden) {
      visibleForConfig.validators = [this.visibleForValidator.bind(this)];
    }
    formConfig.fields.push(visibleForConfig);
  }

  public setVisibleForValidator(isPublic: boolean, visibleForConfig: MultiOptionConfig) {
    if (!isPublic) {
      visibleForConfig.validators = [];
    } else {
      visibleForConfig.validators =  [this.visibleForValidator.bind(this)];
    }
  }

  private visibleForValidator(control: AbstractControl) {

    if (control.value[PoolVisibilityTextEnum.SubContractor] === false && control.value[PoolVisibilityTextEnum.Contractor] === false && control.value[PoolVisibilityTextEnum.Customer] === false) {
      return {
        mydocmaCustomError: this.transalteService.instant('MM.pool.leftPane.form.visibleForError')
      } as CustomValidationError;
    }
    return;
  }

  private whitespaceValidator(control: AbstractControl) {
    if (control.value && typeof control.value === 'string' && control.value.trim().length === 0) {
      return {
        mydocmaCustomError: this.transalteService.instant('MM.pool.leftPane.form.whitespaceError')
      } as CustomValidationError;
    }
    return null;
  }
}
